import { Component, Input, Output, EventEmitter, OnInit, OnChanges, OnDestroy } from '@angular/core';
import { fromEvent, Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormGroup, FormArray, FormBuilder, Validators } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { getColSpan } from '../utils/layout-utils';
import moment from 'moment';
import { Textarea } from "primeng/textarea";
import { InputNumber } from "primeng/inputnumber";
import { MultiSelectModule } from 'primeng/multiselect';
import { ColorPickerModule } from 'primeng/colorpicker';

@Component({
  selector: 'app-dynamic-form',
  templateUrl: './dynamic-form.html',
  styleUrls: ['./dynamic-form.css'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    TableModule,
    CheckboxModule,
    SelectModule,
    DatePickerModule,
    ButtonModule,
    AutoCompleteModule,
    Textarea,
    InputNumber,
    MultiSelectModule,
    ColorPickerModule
]
})
export class DynamicForm implements OnInit, OnChanges, OnDestroy {
  private valueChangesSubscription?: Subscription;
  private isClearingFields = false; // Flag to prevent recursive calls
  
  constructor(private fb: FormBuilder) {}
  @Input() data: any = {};
  @Input() subType : string = '';
  @Input() formGroup: FormGroup = new FormGroup({});
  @Input() tableKey: string = 'tableRows';
  @Input() tableArray!: FormArray;
  @Input() viewMode: boolean = false;
  @Input() isLoading: boolean = false;
  @Input() gridSize: number = 6;
  @Input() regexValidationErrors: Record<string, string> = {};
  @Input() shouldHideField?: (field: any) => boolean; // Function to conditionally hide fields

  @Output() fieldChanged = new EventEmitter<{
    key: string;
    value: any;
    field?: any;
    autoCleared?: boolean;
    reason?: string;
    rowIndex?: number;
    rowId?: any;
  }>();

  @Output() searchQuery = new EventEmitter<{
    fieldName: string;
    query: string;
    field?: any;
  }>();

  @Output() fileUploadRequested = new EventEmitter<{
    file: File;
    field: any;
  }>();

  @Output() fileDownloadRequested = new EventEmitter<{
    s3Key: string;
    fieldName: string;
    field: any;
  }>();

  @Output() buttonClicked = new EventEmitter<{
    fieldName: string;
    actionName: string;
    field: any;
  }>();

  @Input() fileNamesMap: Map<string, string> = new Map(); // Map with key: fieldName, value: file name

  dynamicFormFields: any[] = [];

  filteredMap = new Map<number, any[]>();
  enterSub?: Subscription;
  activeIndex?: number;

  ngOnInit() {
    
    if (Array.isArray(this.data)) {
      this.dynamicFormFields = this.data;
    } else {
      this.dynamicFormFields = [];
    }

    // Auto-populate Yes/No options for boolean radio fields
    this.normalizeBooleanRadioOptions();

    // Ensure all field controls exist in the form group before proceeding
    this.ensureFormControlsExist();

    // Subscribe to form value changes to clear hidden field values
    // Using isClearingFields flag and emitEvent: false to prevent infinite loops
    if (this.formGroup) {
      this.valueChangesSubscription = this.formGroup.valueChanges.subscribe(() => {
        // Prevent recursive calls
        if (!this.isClearingFields) {
          this.clearValuesForHiddenFields();
        }
      });
    }
    this.initializeTable();
    
    // Ensure datetime and month_year fields have proper Date objects
    // Use setTimeout to defer execution and avoid change detection loops
    setTimeout(() => {
      this.ensureDateTimeValues();
    }, 0);
  }

  /**
   * Auto-populate Yes/No options for 'radio_boolean' field type
   * This field type is specifically for boolean Yes/No radio buttons
   */
  private normalizeBooleanRadioOptions(): void {
    this.dynamicFormFields.forEach(field => {
      if (field.field_type === 'radio_boolean') {
        // Auto-populate Yes/No options for radio_boolean field type
        if (!field.options || !Array.isArray(field.options) || field.options.length === 0) {
          field.options = [
            { label: 'Yes', value: true },
            { label: 'No', value: false }
          ];
        }
        // Ensure data_type is set to boolean
        if (!field.data_type) {
          field.data_type = 'boolean';
        }
      }
    });
  }

  ngOnDestroy() {
    if (this.valueChangesSubscription) {
      this.valueChangesSubscription.unsubscribe();
    }
  }

  // Ensure all field controls exist in the form group
  private ensureFormControlsExist(): void {
    if (!this.formGroup || !this.dynamicFormFields || this.dynamicFormFields.length === 0) {
      return;
    }

    this.dynamicFormFields.forEach(field => {
      const fieldKey = this.getFieldKey(field.field_name);
      
      // Skip if control already exists
      if (this.formGroup.get(fieldKey)) {
        return;
      }

      // Create control with default value based on field type and data type
      let defaultValue: any = null;
      
      if (field.default_value !== undefined && field.default_value !== null) {
        defaultValue = field.default_value;
      } else {
        // Set default based on data type
        switch (field.data_type?.toLowerCase()) {
          case 'number':
          case 'integer':
          case 'float':
          case 'double':
            defaultValue = 0;
            break;
          case 'boolean':
          case 'tinyint':
            defaultValue = false;
            break;
          case 'date':
          case 'datetime':
            defaultValue = null;
            break;
          default:
            defaultValue = '';
        }
      }

      // Add validators if field is mandatory
      const validators = field.is_mandatory ? [Validators.required] : [];
      
      // Add the control to the form group
      this.formGroup.addControl(fieldKey, this.fb.control(defaultValue, validators));
    });
  }

  // Method to ensure all datetime and month_year fields have proper Date objects
  private ensureDateTimeValues(): void {
    if (!this.formGroup || !this.formGroup.controls) return;
    
    this.dynamicFormFields.forEach(field => {
      if (field.field_type === 'datetime') {
        const controlName = this.getFieldKey(field.field_name);
        const control = this.formGroup?.get(controlName);
        if (control && control?.value && !(control?.value instanceof Date)) {
          const convertedValue = this.convertToDateValue(control.value, field.field_type);
          control.setValue(convertedValue, { emitEvent: false });
        }
      } else if (field.field_type === 'month_year') {
        const controlName = this.getFieldKey(field.field_name);
        const control = this.formGroup?.get(controlName);
        if (control && control?.value && !(control?.value instanceof Date)) {
          // Convert "March 2025" string to Date object (first day of the month)
          if (typeof control.value === 'string') {
            // Try to parse "Month Year" format (e.g., "March 2025")
            const monthYearMatch = control.value.match(/^(\w+)\s+(\d{4})$/);
            if (monthYearMatch) {
              const [, monthName, yearStr] = monthYearMatch;
              const monthIndex = new Date(`${monthName} 1, 2000`).getMonth();
              if (!isNaN(monthIndex)) {
                const date = new Date(parseInt(yearStr), monthIndex, 1);
                control.setValue(date, { emitEvent: false });
              }
            } else {
              // Fallback: Try to parse YYYY-MM format for backward compatibility
              if (control.value.match(/^\d{4}-\d{2}$/)) {
                const [year, month] = control.value.split('-');
                const date = new Date(parseInt(year), parseInt(month) - 1, 1);
                control.setValue(date, { emitEvent: false });
              }
            }
          }
        }
      }
    });
  }

  ngOnChanges(changes: any) {
    if (changes && changes['data'] && changes['data'].currentValue) {
      if (Array.isArray(this.data)) {
        this.dynamicFormFields = this.data;
      } else {
        this.dynamicFormFields = [];
      }
      
      // Auto-populate Yes/No options for boolean radio fields
      this.normalizeBooleanRadioOptions();
      
      // Ensure all field controls exist when data changes
      this.ensureFormControlsExist();
      
      if (this.formGroup) {
        // Use setTimeout to defer execution and avoid change detection loops
        setTimeout(() => {
          this.ensureDateTimeValues();
        }, 0);
      }
    }

    if (changes && changes['subType'] && changes['subType'].currentValue === 'table') {
      this.initializeTable();
    }

    // Handle tableArray changes - when parent provides populated FormArray
    if (changes && changes['tableArray'] && changes['tableArray'].currentValue) {
      // If we receive a populated FormArray, use it directly
      if (this.tableArray && this.tableArray.length > 0) {
        // Ensure datetime values in table rows are proper Date objects
        setTimeout(() => this.ensureTableDateTimeValues(), 0);
      }
    }
  }

  // Method to ensure datetime fields in table rows have proper Date objects
  private ensureTableDateTimeValues(): void {
    if (!this.tableArray) return;
    
    this.tableArray.controls.forEach(rowControl => {
      if (rowControl instanceof FormGroup) {
        this.data.forEach((field: any) => {
          if (field.field_type === 'datetime') {
            const controlName = this.getFieldKey(field.field_name);
            const control = rowControl?.get(controlName);
            if (control && control.value && !(control.value instanceof Date)) {
              const convertedValue = this.convertToDateValue(control.value, field.field_type);
              control.setValue(convertedValue, { emitEvent: false });
            }
          }
        });
      }
    });
  }

  getTableRowsArray(): FormArray {
  // Always prioritize the tableArray input from parent
  if (this.tableArray) {
    return this.tableArray;
  }
  
  // Fallback to formGroup control
  const formArray = this.formGroup?.get(this.tableKey) as FormArray;
  if (formArray) {
    return formArray;
  }

  return this.fb.array([]);
}


private initializeTable() {
 if (this.subType === 'table' && this.formGroup) {

   // Only add control if it doesn't exist AND tableArray wasn't provided
   if (!this.tableArray && !this.formGroup?.get(this.tableKey)) {
     this.formGroup.addControl(this.tableKey, this.fb.array([]));
   }

   if (this.getTableRowsArray().length === 0) {
     this.addTableRow();
   }
 }
}

  getEmptyMessageColspan(): number {
    const dataColumns = Array.isArray(this.data)
      ? this.data.filter((col: any) => col.field_name !== 'is_selected').length
      : 0;
    const extraColumns = this.viewMode ? 0 : 2;
    return dataColumns + extraColumns;
  }

  getVisibleTableRows() {
    const visibleRows = this.getTableRowsArray().controls.filter(control => {
      const isDeletedControl = control?.get('is_deleted');
      const isDeleted = isDeletedControl?.value;
      // Hide rows where is_deleted is 1, true, or any truthy value
      return !isDeleted;
    });
    return visibleRows;
  }


  getFieldKey(fieldName: string): string {
    return fieldName?.toLowerCase().replace(/[^a-z0-9]/g, '_');
  }

  // Helper method to convert string values to proper Date objects for date/datetime fields
  private convertToDateValue(value: any, fieldType: string): any {
    if (!value || value === '') return null;
    
    try {
      if (fieldType === 'date' || fieldType === 'datetime') {
        if (value instanceof Date && !isNaN(value.getTime())) return value;
        
        if (typeof value === 'string') {
          // For datetime/time fields, handle time-only strings
          if (fieldType === 'datetime') {
            // If it's just a time string like "14:30"
            if (value.match(/^\d{1,2}:\d{2}$/)) {
              const today = new Date();
              const [hours, minutes] = value.split(':');
              today.setHours(parseInt(hours), parseInt(minutes), 0, 0);
              return today;
            }
          }
          
          // Try to parse as a regular date/datetime
          const parsedDate = new Date(value);
          return isNaN(parsedDate.getTime()) ? null : parsedDate;
        }
      }
    } catch (error) {
      console.warn('Error converting date value:', value, error);
      return null;
    }
    
    return value;
  }

  // Method to get safe form control value for datetime fields
  getSafeFormControlValue(fieldName: string, fieldType: string): any {
    const control = this.formGroup?.get(this.getFieldKey(fieldName));
    if (!control) return null;
    
    const value = control.value;
    if (fieldType === 'datetime') {
      const convertedValue = this.convertToDateValue(value, fieldType);
      if (convertedValue !== value) {
        control.setValue(convertedValue, { emitEvent: false });
      }
      return convertedValue;
    }
    
    return value;
  }

  onFieldChange(fieldName: string, event: any, field?: any) {
    const value = event?.value ?? (event?.target?.type === 'checkbox' ? event?.target?.checked : event?.target?.value) ?? event;

    const key = this.getFieldKey(fieldName);
    
    // Find the field object if not provided
    if (!field && this.data) {
      field = this.data.find((f: any) => f.field_name === fieldName);
    }
    
    // For table fields, emit the entire table data
    if (this.subType === 'table') {
      this.emitTableChange();
    } else {
      this.fieldChanged.emit({ key, value, field });
    }

    this.clearValuesForHiddenFields(fieldName);
  }

  onMonthYearChange(fieldName: string, event: any) {
    const date = event instanceof Date ? event : (event?.value || event);
    const key = this.getFieldKey(fieldName);
    const control = this.formGroup?.get(key);
    
    if (date instanceof Date) {
      // Convert Date to "March 2025" format for storage
      const monthName = date.toLocaleString('default', { month: 'long' });
      const year = date.getFullYear();
      const value = `${monthName} ${year}`;
      
      // Update the form control with the Date object (for display) but emit "Month Year" format
      if (control) {
        control.setValue(date, { emitEvent: false });
      }
      
      // Find the field object
      const field = this.data?.find((f: any) => f.field_name === fieldName);
      
      // For table fields, emit the entire table data
      if (this.subType === 'table') {
        this.emitTableChange();
      } else {
        // Emit the "Month Year" format value
        this.fieldChanged.emit({ key, value, field });
      }
      
      this.clearValuesForHiddenFields(fieldName);
    } else {
      // Fallback to regular onFieldChange
      this.onFieldChange(fieldName, event);
    }
  }

  onTableFieldChange(rowIndex: number, fieldName: string, event: any) {
    const value = event?.value ?? (event?.target?.type === 'checkbox' ? event?.target?.checked : event?.target?.value) ?? event;
    const key = this.tableKey;
    const rowsArray = this.getTableRowsArray();
    const rowId = (rowsArray.at(rowIndex) as FormGroup)?.get('id')?.value ?? null;
    this.fieldChanged.emit({ key, value: rowsArray.value, reason: 'table', rowIndex, rowId });
    setTimeout(() => this.clearValuesForHiddenFields(fieldName), 0);
  }

  shouldShowField(field: any): boolean {
    // Check is_visible flag from backend first
    if (field.is_visible === false) {
      return false;
    }
    
    // Check if form control exists for this field
    if (!this.formGroup || !this.formGroup.controls) {
      return false;
    }
    
    const fieldKey = this.getFieldKey(field.field_name);
    if (!this.formGroup.get(fieldKey)) {
      return false; // Don't show field if control doesn't exist
    }
    
    // Check custom hide function from parent component
    if (this.shouldHideField && this.shouldHideField(field)) {
      return false;
    }
    
    if (!field.show_if) return true;
    const depKey = this.getFieldKey(field.show_if.field);
    const depControl = this.formGroup?.get(depKey);
    if (!depControl) return true; // If parent field doesn't exist, show the field
    
    const expectedValue = field.show_if.value;
    
    let depValue = depControl.value;
    if (depValue && typeof depValue === 'object' && !(depValue instanceof Date) && !Array.isArray(depValue)) {
      depValue = depValue.id ?? depValue.value ?? depValue;
    }

    switch (typeof expectedValue) {
      case 'boolean':
        const boolValue = depValue === true || depValue === 1 || depValue === '1' || depValue === 'true';
        return boolValue === expectedValue;
      case 'number':
        const numValue = typeof depValue === 'number' ? depValue : Number(depValue);
        return !isNaN(numValue) && numValue === expectedValue;
      case 'string':
        const strValue = String(depValue || '').toLowerCase();
        const strExpected = expectedValue.toLowerCase();
        return strValue === strExpected;
      default:
        return depValue === expectedValue;
    }
  }

  getColSpan(field: any): number {
    return getColSpan(field?.width, this.gridSize);
  }
  
  getGridTemplateColumns(): string {
    return `repeat(${this.gridSize}, minmax(0, 1fr))`;
  }
  
  // Helper methods for view mode and readonly fields
  getFieldDisplayValue(field: any, formControlName?: string): string {
    // Check if form control exists
    if (!this.formGroup || !this.formGroup.controls) {
      return '-';
    }
    
    // Allow readonly fields to display even in edit mode
    if (!this.viewMode && field.field_type != 'label') return '';
    
    const fieldKey = formControlName || this.getFieldKey(field.field_name);
    const control = this.formGroup.get(fieldKey);
    
    if (!control) {
      return '-'; // Return default value if control doesn't exist
    }
    
    const value = control.value;
    
    return this.formatDisplayValue(value, field);
  }

  getTableCellDisplayValue(field: any, rowGroup: FormGroup): string {
    if (!rowGroup) return '';
    
    const control = rowGroup?.get(this.getFieldKey(field.field_name));
    const value = control?.value;
    
    return this.formatDisplayValue(value, field);
  }

  private formatDisplayValue(value: any, field: any): string {
    if (field.field_type === 'label' && (!value || value === '')) {
      return '-';
    }

    // For label fields, format based on data_type
    if (field.field_type === 'label') {
      switch (field.data_type) {
        case 'date':
          if (value instanceof Date) {
            return moment(value).format('DD/MM/YYYY');
          } else if (typeof value === 'string') {
            const momentDate = moment(value);
            return momentDate.isValid() ? momentDate.format('DD/MM/YYYY') : value;
          }
          return value?.toString();
        
        case 'datetime':
          if (value instanceof Date) {
            return moment(value).format('DD/MM/YYYY HH:mm');
          } else if (typeof value === 'string') {
            const momentDate = moment(value, ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DDTHH:mm:ss', 'YYYY-MM-DD HH:mm']);
            return momentDate.isValid() ? momentDate.format('DD/MM/YYYY HH:mm') : value;
          }
          return value?.toString();
        
        case 'number':
          if (typeof value === 'number') {
            return value.toString();
          } else if (typeof value === 'string' && !isNaN(parseFloat(value))) {
            return parseFloat(value).toString();
          }
          return value?.toString();
        
        case 'boolean':
        case 'tinyint':
          if (typeof value === 'boolean') {
            return value ? 'Yes' : 'No';
          } else if (typeof value === 'string') {
            return (value.toLowerCase() === 'true' || value === '1' || value.toLowerCase() === 'yes') ? 'Yes' : 'No';
          } else if (typeof value === 'number') {
            return value ? 'Yes' : 'No';
          }
          return value?.toString();
        
        default:
          // For label with select options, try to find the option label
          if (field.options && field.options.length > 0) {
            const option = field.options.find((opt: any) => opt.id == value || opt.value == value);
            if (option) {
              return option.name || option.label || value;
            }
          }
          return value?.toString();
      }
    }

    switch (field.field_type) {
      case 'autocomplete': {
        // Handle autocomplete fields - value might be an object with label property
        if (value && typeof value === 'object') {
          // If value is an object, extract the label
          const displayField = field.autocompleteDisplayField || 'label';
          return value[displayField] || value.label || value.name || value.company_name || value.value || '-';
        }
        // If value is just an ID, try to find it in options
        if (field.options && field.options.length > 0) {
          const option = field.options.find((opt: any) => 
            opt.id === value || opt.value === value || String(opt.id) === String(value) || String(opt.value) === String(value)
          );
          if (option) {
            const displayField = field.autocompleteDisplayField || 'label';
            return option[displayField] || option.label || option.name || option.company_name || value;
          }
        }
        return value?.toString() || '-';
      }
      
      case 'select':
        case 'label': {
          // For readonly select fields, try to find the option label
          if (field.options && field.options.length > 0) {
            const option = field.options.find((opt: any) => opt.id === value || opt.value === value);
            if (option) {
              return option.name || option.label || option.company_name || value;
            }
          }
          // For readonly number fields, format as number
          if (field.data_type === 'number') {
            return typeof value === 'number' ? value.toString() : value;
          }
          return value?.toString();
        }

      case 'multiselect': {
        // Resolve each selected value to its option label and join with ", "
        if (!Array.isArray(value) || value.length === 0) return '-';
        const opts = field.options || [];
        const labels = value.map((v: any) => {
          const opt = opts.find((o: any) =>
            o.id === v || o.value === v || String(o.id) === String(v) || String(o.value) === String(v)
          );
          return opt ? (opt.name || opt.label || opt.company_name || String(v)) : String(v);
        });
        return labels.join(', ');
      }
      
      case 'checkbox':
        return value ? 'Yes' : 'No';
      case 'radio_boolean':
        // Handle null/undefined - return empty
        if (value === null || value === undefined || value === '') {
          return '-';
        }
        if (typeof value === 'boolean') {
          return value ? 'Yes' : 'No';
        } else if (typeof value === 'string') {
          return (value.toLowerCase() === 'true' || value === '1') ? 'Yes' : 'No';
        }
        return value ? 'Yes' : 'No';
      
      case 'date':
        const dateFmt = field.show_time || field.showTime ? 'DD/MM/YYYY hh:mm A' : 'DD/MM/YYYY';
        if (value instanceof Date) {
          if (isNaN(value.getTime())) {
            return '-';
          }
          const momentDate = moment(value);
          return momentDate.isValid() ? momentDate.format(dateFmt) : '-';
        } else if (typeof value === 'string') {
          const momentDate = moment(value);
          return momentDate.isValid() ? momentDate.format(dateFmt) : '-';
        }
        return '-';
      
      case 'datetime':
        if (value instanceof Date) {
          return moment(value).format('HH:mm');
        } else if (typeof value === 'string') {
          const momentDate = moment(value, ['HH:mm', 'HH:mm:ss', 'YYYY-MM-DD HH:mm:ss']);
          return momentDate.isValid() ? momentDate.format('HH:mm') : value;
        }
        return value;
      
      case 'month_year': {
        // Handle Date object from datepicker
        if (value instanceof Date) {
          const monthName = value.toLocaleString('default', { month: 'long' });
          const year = value.getFullYear();
          return `${monthName} ${year}`;
        }
        // Handle "Month Year" string format (e.g., "March 2025")
        if (value && typeof value === 'string') {
          // If already in "Month Year" format, return as is
          if (value.match(/^\w+\s+\d{4}$/)) {
            return value;
          }
          // Fallback: Try to parse YYYY-MM format for backward compatibility
          if (value.match(/^\d{4}-\d{2}$/)) {
            const [year, month] = value.split('-');
            if (year && month) {
              const date = new Date(parseInt(year), parseInt(month) - 1, 1);
              const monthName = date.toLocaleString('default', { month: 'long' });
              return `${monthName} ${year}`;
            }
          }
        }
        return value?.toString();
      }
      
      case 'number':
      case 'double':
        return typeof value === 'number' ? value.toString() : value;
      
      default:
        return value?.toString();
    }
  }

  private clearValuesForHiddenFields(triggerField?: string) {
    if (!this.formGroup || this.isClearingFields) return;
    
    this.isClearingFields = true;
    
    try {
      this.dynamicFormFields.forEach(field => {
        if (field.show_if) {
          const shouldShow = this.shouldShowField(field);
          const fieldKey = this.getFieldKey(field.field_name || field.name);
          const control = this.formGroup?.get(fieldKey);

          if (!shouldShow && control?.value !== null && control?.value !== undefined && control?.value !== '') {
            const previousValue = control.value;
            // Use emitEvent: false to prevent triggering valueChanges again
            control.setValue(null, { emitEvent: false });

            this.fieldChanged.emit({
              key: fieldKey,
              value: null,
              autoCleared: true,
              reason: `${field.show_if?.field || 'parent'} changed (previous value: ${previousValue})`
            });
          }
        }
      });
    } finally {
      this.isClearingFields = false;
    }
  }

addTableRow() {
  const row = this.fb.group({});
  
  // Add ID and deletion tracking for new rows
  row.addControl('id', this.fb.control(null));
  row.addControl('is_deleted', this.fb.control(false));
  
  this.data.forEach((field: any) => {
    const key = this.getFieldKey(field.field_name);
    const validators = field.is_mandatory ? [Validators.required] : [];
    const def = this.getDefaultValueForDataType(field.data_type);
    row.addControl(key, this.fb.control(def, validators));
  });
  
  this.getTableRowsArray().push(row);
  
  // Force the formArray to update
  this.getTableRowsArray().updateValueAndValidity();
  
  this.emitTableChange();
}

private getDefaultValueForDataType(dataType: string): any {
    switch (dataType?.toLowerCase()) {
      case 'number':
      case 'decimal':
      case 'float':
      case 'double':
        return 0;
      case 'boolean':
        return false;
      case 'date':
      case 'datetime':
        return null;
      case 'array':
      case 'json':
        return [];
      case 'object':
        return null;
      default:
        return '';
    }
  }

private emitTableChange() {
  this.fieldChanged.emit({
    key: this.tableKey,
    value: this.getTableRowsArray().value
  });
}

  onlyNumbers(event: KeyboardEvent): void {
    // Allow only digit keys (0-9); keypress naturally excludes control keys
    if (event.key < '0' || event.key > '9') {
      event.preventDefault();
    }
  }

  stripNonDigits(event: Event, field: any): void {
    const input = event.target as HTMLInputElement;
    const stripped = input.value.replace(/[^0-9]/g, '');
    if (stripped !== input.value) {
      input.value = stripped;
      const control = this.formGroup?.get(this.getFieldKey(field.field_name));
      if (control) control.setValue(stripped, { emitEvent: false });
    }
    this.onFieldChange(field.field_name, event);
  }

  onAutocompleteSearch(event: { query: string }, field: any) {
    // Emit search query to parent component
    this.searchQuery.emit({
      fieldName: field.field_name,
      query: event.query,
      field: field
    });
  }

  onAutocompleteComplete(event: { query: string }, field: any) {
    this.onAutocompleteSearch(event, field);
  }

  onAutocompleteKeydown(event: KeyboardEvent, field: any) {
    // Optional: Allow Enter key to also trigger search (in addition to typeahead)
    if (event.key === 'Enter') {
      event.preventDefault(); // Prevent form submission
      
      // Get the current input value from the event target
      const inputElement = event.target as HTMLInputElement;
      const query = inputElement?.value || '';
      
      // Trigger search with current input value
      this.onAutocompleteSearch({ query }, field);
    }
  }

  onFileSelected(event: any, field: any) {
    const fileInput = event.target;
    const files = fileInput.files;

    if (files && files.length > 0) {
      const file = field.multiple ? Array.from(files) : files[0];

      // Emit file upload request to parent component for S3 upload
      this.fileUploadRequested.emit({
        file: file,
        field: field
      });
    }
  }

  onButtonClick(field: any) {
    this.buttonClicked.emit({
      fieldName: field.field_name,
      actionName: field.action_name || field.field_name,
      field: field
    });
  }

  getSelectedFileName(field: any): string {
    const fieldKey = this.getFieldKey(field.field_name);
    
    // First, check the fileNamesMap for this specific field
    const fileNameFromMap = this.fileNamesMap.get(fieldKey);
    if (fileNameFromMap && fileNameFromMap.length > 0) {
      return fileNameFromMap;
    }

    // Fallback: check the file field itself
    const control = this.formGroup?.get(fieldKey);
    const value = control?.value;

    if (!value) {
      return '';
    }

    if (field.multiple && Array.isArray(value)) {
      return value.map((file: File) => file.name).join(', ');
    } else if (value instanceof File) {
      return value.name;
    } else if (typeof value === 'string' && value.length > 0) {
      // Handle s3_key or file path - extract filename from path
      const parts = value.split('/');
      return parts[parts.length - 1] || value;
    }

    return '';
  }

  /**
   * Check if file field has a value (either File object or s3_key string)
   */
  hasFileValue(field: any): boolean {
    const fieldKey = this.getFieldKey(field.field_name);
    const control = this.formGroup?.get(fieldKey);
    const value = control?.value;

    if (!value) {
      return false;
    }

    if (value instanceof File) {
      return true;
    }

    if (typeof value === 'string' && value.length > 0) {
      return true;
    }

    return false;
  }

  /**
   * Get the file name from an S3 key
   */
  getFileNameFromS3Key(field: any): string {
    const fieldKey = this.getFieldKey(field.field_name);
    
    // First check if we have a custom file name in the map
    const customFileName = this.fileNamesMap.get(fieldKey);
    if (customFileName) {
      return customFileName;
    }

    // Otherwise extract from S3 key
    const control = this.formGroup?.get(fieldKey);
    const s3Key = control?.value;
    
    if (!s3Key || typeof s3Key !== 'string') {
      return '';
    }

    // Extract filename from S3 key (e.g., "1/vendor/file_20251128_abc.pdf" -> "file_20251128_abc.pdf")
    const parts = s3Key.split('/');
    return parts[parts.length - 1] || s3Key;
  }


  /**
   * Open download URL in new tab
   */
  openDownloadUrl(field: any): void {
    const fieldKey = this.getFieldKey(field.field_name);
    const control = this.formGroup?.get(fieldKey);
    const s3Key = control?.value;

    if (!s3Key || typeof s3Key !== 'string' || s3Key.length === 0) {
      console.warn('No S3 key found in the file field');
      return;
    }

    // Emit event to parent to handle download URL fetching
    this.fileDownloadRequested.emit({
      s3Key: s3Key,
      fieldName: field.field_name,
      field: field
    });
  }

  removeFile(field: any, fileInput: any) {
    const fieldKey = this.getFieldKey(field.field_name);
    const control = this.formGroup?.get(fieldKey);

    if (control) {
      control.setValue(null);
      control.markAsTouched();
    }

    // Clear file name from map
    this.fileNamesMap.delete(fieldKey);

    // Clear the file input
    if (fileInput) {
      fileInput.value = '';
    }

    // Emit field change event
    this.fieldChanged.emit({
      key: fieldKey,
      value: null,
      field: field
    });
  }

  removeTableRow(index: number) {
    const tableArray = this.getTableRowsArray();
    
    if (index >= 0 && index < tableArray.length) {
      const rowGroup = tableArray.at(index) as FormGroup;
      if (!rowGroup) return;
      
      // Always mark as deleted instead of removing (set is_deleted to 1)
      const isDeletedControl = rowGroup?.get('is_deleted');
      if (!isDeletedControl) {
        // If is_deleted control doesn't exist, add it
        rowGroup.addControl('is_deleted', this.fb.control(1));
      } else {
        isDeletedControl.setValue(1);
      }
      
      // Emit field change event
      this.emitTableChange();
    }
  }

  cacheFilter(i: number) {
    setTimeout(() => {
      const overlay = document.querySelector('.p-select-overlay');
      if (!overlay) return;
  
      const options = overlay.querySelectorAll('.p-select-option:not(.p-disabled)');
      this.filteredMap.set(i, Array.from(options));
    });
  }

  onShow(i: number) {
    this.activeIndex = i;
    setTimeout(() => {
      const input = document.querySelector('.p-select-filter') as HTMLInputElement;
      if (!input) return;
  
      this.enterSub = fromEvent<KeyboardEvent>(input, 'keydown').subscribe(e => {
        if (e.key === 'Tab') this.tryAutoPick();
      });
    });
  }

  onHide(i: number) {
    if (this.activeIndex === i) {
      this.enterSub?.unsubscribe();
      this.enterSub = undefined;
      this.activeIndex = undefined;
    }
  }

  tryAutoPick() {
    if (this.activeIndex == null) return;

    const options = this.filteredMap.get(this.activeIndex) || [];
    if (options.length === 1) {
      (options[0] as HTMLElement).click();
    }
  }
}
